import Phaser from 'phaser';

export const GAME_WIDTH = 1024;
export const GAME_HEIGHT = 768;

export const COLORS = {
  desktopBackground: 0x0f1420,
  menuBar: 0x1a1f2e,
  menuBarText: 0xe6e9f0,
  menuBarDivider: 0x2a3040,
  windowChrome: 0x2a3040,
  windowTitleBar: 0x3a4050,
  windowTitleText: 0xffffff,
  windowBody: 0x1e2432,
  windowBorder: 0x4a5060,
  emailUnreadDot: 0x60a5fa,
  emailSubject: 0xe6e9f0,
  emailMeta: 0x8a95a8,
  emailSeparator: 0x2a3040,
  buttonPrimary: 0x3a82f7,
  buttonPrimaryText: 0xffffff,
  buttonHover: 0x4a92ff,
  accentWarn: 0xfbbf24,
  accentDanger: 0xef4444,
  textPrimary: 0xffffff,
  textMuted: 0x8a95a8,
  black: 0x000000,
  windowControlClose: 0xef4444,
  windowControlMinimize: 0xfbbf24,
  windowControlMaximize: 0x22c55e,
  badgeLogged: 0x22c55e,
  success: 0x7fb77e,
  warning: 0xd07474,
  taskbarBg: 0x1a1d24,
  taskbarBorder: 0x3a4050,
  taskbarTileBg: 0x2a2f3c,
  taskbarTileHover: 0x3a4152,
  taskbarTileText: 0xdfe4ee,
  taskbarBadgeBg: 0xe04040,
  taskbarBadgeText: 0xffffff,
  taskbarPulse: 0xfbbf24,
  markedTermUnderline: 0x60a5fa,
  markedTermHighlight: 0xfbbf24,
  windowShadow: 0x000000,
  windowTitleHighlight: 0xffffff,
  controlRingMaximize: 0x166534,
  controlRingMinimize: 0x854d0e,
  controlRingClose: 0x7f1d1d,
};

export const FONTS = {
  ui: '"Inter", system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, Menlo, Consolas, monospace',
};

export const toHexColor = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

export const MENU_BAR = {
  height: 32,
  paddingX: 16,
  clockX: 16,
  unreadX: 130,
  slackX: 220,
  dsoX: 320,
  scoreX: 410,
  muteX: 510,
  pauseHintX: 560,
  settingsX: 990,
};

export const INBOX_WINDOW = {
  x: 80,
  y: 72,
  width: 540,
  height: 560,
  titleBarHeight: 28,
  emailRowHeight: 48,
  emailPaddingX: 16,
  tabBarHeight: 28,
  tabFontSize: '13px',
  tabPaddingX: 16,
  tabGap: 24,
};

export const SLACK_WINDOW = {
  x: 660,
  y: 72,
  width: 320,
  height: 360,
  titleBarHeight: 28,
  rowPaddingX: 12,
  rowPaddingY: 8,
  authorFontSize: '12px',
  bodyFontSize: '12px',
  timestampFontSize: '10px',
};

export const SPREADSHEET_WINDOW = {
  x: 80,
  y: 72,
  width: 880,
  height: 560,
  titleBarHeight: 28,
  tabBarHeight: 32,
  rowHeight: 22,
  headerHeight: 26,
  paddingX: 12,
  columnFontSize: '11px',
  rowFontSize: '12px',
  headerFontSize: '11px',
  bucketFontSize: '13px',
  bucketLabelFontSize: '11px',
};

export const TEACH_PANEL = {
  width: 380,
  height: 260,
  paddingX: 20,
  paddingY: 20,
  titleFontSize: '20px',
  bodyFontSize: '13px',
  dismissButtonHeight: 36,
  dismissButtonText: 'Got it',
};

export const DUNNING_MODAL = {
  width: 520,
  height: 420,
  paddingX: 24,
  paddingY: 20,
  titleFontSize: '18px',
  promptFontSize: '14px',
  subpromptFontSize: '12px',
  templateLabelFontSize: '14px',
  templateBlurbFontSize: '12px',
  templateRowHeight: 64,
  templateRowGap: 8,
  cancelButtonHeight: 32,
  cancelButtonFontSize: '12px',
  toastFontSize: '13px',
  toastDurationMs: 2000,
  backdropAlpha: 0.55,
};

export const CASH_APP_MODAL = {
  width: 720,
  height: 640,
  paddingX: 24,
  paddingY: 20,
  titleFontSize: '18px',
  wireSummaryFontSize: '16px',
  remittanceFontSize: '12px',
  promptFontSize: '14px',
  customerRowHeight: 26,
  customerRowGap: 2,
  customerFontSize: '12px',
  invoiceRowHeight: 36,
  invoiceRowGap: 3,
  invoiceFontSize: '12px',
  counterFontSize: '14px',
  counterBoxHeight: 48,
  confirmButtonHeight: 36,
  confirmButtonFontSize: '13px',
  backButtonFontSize: '12px',
  toastFontSize: '13px',
  toastDurationMs: 2000,
  backdropAlpha: 0.55,
};

export const SHORT_PAY_MODAL = {
  width: 520,
  height: 420,
  paddingX: 24,
  paddingY: 20,
  titleFontSize: '18px',
  wireSummaryFontSize: '14px',
  subpromptFontSize: '14px',
  choiceLabelFontSize: '14px',
  choiceBlurbFontSize: '12px',
  choiceRowHeight: 76,
  choiceRowGap: 10,
  cancelButtonHeight: 32,
  cancelButtonFontSize: '12px',
  toastFontSize: '13px',
  toastDurationMs: 2000,
  backdropAlpha: 0.55,
};

export const DISPUTE_FORM_MODAL = {
  width: 560,
  height: 520,
  paddingX: 24,
  paddingY: 20,
  titleFontSize: '18px',
  subpromptFontSize: '14px',
  choiceRowHeight: 64,
  choiceRowGap: 8,
  choiceLabelFontSize: '14px',
  choiceBlurbFontSize: '12px',
  radioRadius: 8,
  radioInnerRadius: 4,
  radioMarginX: 12,
  radioMarginY: 14,
  amountLabelFontSize: '13px',
  amountValueFontSize: '16px',
  submitButtonWidth: 130,
  submitButtonHeight: 36,
  submitButtonFontSize: '14px',
  cancelButtonWidth: 100,
  cancelButtonHeight: 36,
  cancelButtonFontSize: '13px',
  buttonGap: 10,
  toastFontSize: '13px',
  toastDurationMs: 2000,
  backdropAlpha: 0.55,
  readerFooterHeight: 48,
  readerFooterButtonWidth: 130,
  readerFooterButtonHeight: 32,
  readerFooterFontSize: '13px',
  inboxBadgeWidth: 72,
  inboxBadgeHeight: 20,
  inboxBadgeFontSize: '11px',
};

export const PAYMENTS_WINDOW = {
  x: 700,
  y: 110,
  width: 320,
  height: 280,
  titleBarHeight: 28,
  rowHeight: 60,
  rowPaddingX: 12,
  rowPaddingY: 8,
  amountFontSize: '15px',
  customerFontSize: '11px',
  ageFontSize: '10px',
  buttonFontSize: '12px',
  buttonWidth: 64,
  buttonHeight: 24,
  expirationMs: 180_000, // 3 minutes
};

export const EMAIL_ACTION_FOOTER = {
  height: 56,
  buttonHeight: 32,
  buttonGap: 8,
  buttonFontSize: '13px',
  /** Per-button widths (label widths differ; we'd rather not jam everything to one). */
  buttonWidthByAction: {
    reply: 96,
    archive: 96,
    'forward-collections': 168,
    acknowledge: 96,
  },
};

export const FIELD_GUIDE_WINDOW = {
  x: 612,
  y: 200,
  width: 400,
  height: 520,
  titleBarHeight: 28,
  // Layout knobs — rows are variable height now (locked is compact,
  // unlocked grows to fit the wrapped definition).
  rowPaddingX: 16,
  rowPaddingY: 14,
  rowGap: 1,         // divider thickness between rows
  unlockedTitleFontSize: '14px',
  unlockedDefinitionFontSize: '12px',
  unlockedDefinitionLineSpacing: 3,
  unlockedTimestampFontSize: '11px',
  lockedRowHeight: 32,
  lockedFontSize: '12px',
  sectionHeaderFontSize: '11px',
  sectionTopGap: 10,
  sectionBottomGap: 8,
  // Legacy fields kept so older callers (tests) don't break — both
  // resolve to the new tokens.
  rowHeight: 72,
  termTitleFontSize: '14px',
  termDefinitionFontSize: '12px',
  termTimestampFontSize: '11px',
  lockedColor: 0x4a5060,
  highlightFlashMs: 1200,
};

export const SETTINGS_MODAL = {
  width: 480,
  height: 420,
  paddingX: 24,
  paddingY: 20,
  titleFontSize: '18px',
  rowFontSize: '13px',
  rowHeight: 38,
  rowGap: 6,
  buttonWidth: 64,
  buttonHeight: 28,
  buttonGap: 6,
  buttonFontSize: '11px',
  pauseButtonHeight: 36,
  closeButtonSize: 24,
  backdropAlpha: 0.55,
};

export const TOOLTIP = {
  paddingX: 8,
  paddingY: 6,
  fontSize: '11px',
  maxWidth: 240,
  pointerHeight: 5,
  bgColor: 0x000000,
  bgAlpha: 0.92,
  textColor: 0xffffff,
};

export const COLLECTIONS_CALL_MODAL = {
  width: 600,
  height: 480,
  paddingX: 24,
  paddingY: 20,
  headerHeight: 60,
  avatarRadius: 20,
  callerNameFontSize: '16px',
  callerSubtitleFontSize: '12px',
  hangupButtonWidth: 80,
  hangupButtonHeight: 28,
  hangupButtonFontSize: '12px',
  customerLineFontSize: '14px',
  customerLineLineSpacing: 4,
  optionRowHeight: 56,
  optionRowGap: 8,
  optionFontSize: '13px',
  ptpFormTitleFontSize: '16px',
  ptpFieldLabelFontSize: '13px',
  ptpFieldHeight: 36,
  ptpFieldFontSize: '13px',
  ptpFieldGap: 12,
  ptpSubmitButtonHeight: 36,
  ptpSubmitButtonFontSize: '14px',
  ptpSkipButtonFontSize: '12px',
  toastFontSize: '13px',
  toastDurationMs: 2000,
  backdropAlpha: 0.55,
};

export const WRITE_OFF_MODAL = {
  width: 520,
  height: 380,
  paddingX: 24,
  paddingY: 20,
  titleFontSize: '18px',
  subtitleFontSize: '13px',
  consequenceFontSize: '14px',
  consequencePaddingY: 12,
  bodyFontSize: '13px',
  buttonHeight: 36,
  buttonFontSize: '14px',
  buttonGap: 12,
  toastFontSize: '13px',
  toastDurationMs: 2000,
  backdropAlpha: 0.55,
};

export const DSO_MODAL = {
  width: 480,
  height: 360,
  paddingX: 28,
  paddingY: 24,
  titleFontSize: '24px',
  metricFontSize: '14px',
  metricGap: 6,
  explainerFontSize: '13px',
  explainerPaddingY: 16,
  contextFontSize: '12px',
  buttonHeight: 36,
  buttonFontSize: '14px',
  countUpDurationMs: 700,
  backdropAlpha: 0.55,
};

export const FIELD_GUIDE = {
  cardWidth: 560,
  cardHeight: 320,
  cardY: GAME_HEIGHT / 2,
  titleSize: 28,
  definitionSize: 16,
  empathySize: 14,
  captionSize: 12,
  captionMarginTop: 24,
  cardFadeIn: 400,
  cardHold: 2200,
  cardFadeOut: 400,
  perCardTotal: 3000,
  skipHintY: GAME_HEIGHT - 30,
  skipHintX: GAME_WIDTH - 60,
} as const;

export const SCORECARD = {
  titleY: 120,
  titleSize: 32,
  rowStartY: 220,
  rowGap: 48,
  rowLabelX: GAME_WIDTH / 2 - 200,
  rowValueX: GAME_WIDTH / 2 - 20,
  rowOkX: GAME_WIDTH / 2 + 140,
  rowFadeIn: 500,
  rowStagger: 1000,
  closingY: GAME_HEIGHT - 140,
  closingSize: 18,
  closingDelay: 7000,
  autoAdvanceMs: 13000,
} as const;

export const APP_WINDOW = {
  titlePaddingX: 14,
  borderWidth: 1,
  controlDotRadius: 6,
  controlDotInnerRadius: 5,
  controlDotSpacing: 20,
  controlRightMargin: 16,
  minWidth: 280,
  minHeight: 180,
  resizeHandleSize: 14,
  shadowOffsetY: 10,
  shadowSpread: 6,
  shadowAlpha: 0.45,
  titleHighlightAlpha: 0.06,
};

export const TASKBAR = {
  height: 36,
  y: GAME_HEIGHT - 36,
  marginX: 12,
  tileWidth: 160,
  tileHeight: 28,
  tileGap: 8,
  tileIconSize: 14,
  tileIconPaddingX: 8,
  tileLabelPaddingX: 30,
  tileLabelFontSize: '12px',
  badgeRadius: 9,
  badgePaddingRight: 10,
  badgeFontSize: '11px',
  pulseRingRadius: 14,
  pulseRingBaseWidth: 1,
  pulseRingMaxScale: 1.6,
  pulseLoopMs: 800,
  pulseHoldAfterEventMs: 3000,
  pulseHoldSpreadsheetMs: 2000,
  tileLayoutTweenMs: 180,
  windowFadeMs: 150,
} as const;

export const DEPTHS = {
  desktop: 0,
  appBody: 10,
  appFocused: 20,
  menuBar: 100,
  taskbar: 150,
  modal: 200,
  teachPanel: 300,
  ending: 400,
};

export const gameConfig: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game-container',
  width: GAME_WIDTH,
  height: GAME_HEIGHT,
  backgroundColor: COLORS.desktopBackground,
  physics: {
    default: 'arcade',
    arcade: {
      gravity: { x: 0, y: 0 },
      debug: false,
    },
  },
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  render: {
    pixelArt: false,
    antialias: true,
    roundPixels: true,
  },
};
